import React, { useEffect } from "react";
import {
  loadQuizAssignedToMe,
  loadQuizzes,
  loadQuizzesAttemptedFreelyByMe,
  selectors,
  useAppDispatch,
  useAppSelector,
} from "../../../state";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";

import { ShowLoading } from "../../handlers/ShowLoading";
import { QuizAttemptDTO, QuizSummaryDTO, RegisteredUserDTO } from "../../../../IsaacApiTypes";
import { TitleAndBreadcrumb } from "../../elements/TitleAndBreadcrumb";
import { formatDate } from "../../elements/DateString";
import { AppQuizAssignment } from "../../../../IsaacAppTypes";
import {
  extractTeacherName,
  isAttempt,
  isFound,
  isTutorOrAbove,
  partitionCompleteAndIncompleteQuizzes,
} from "../../../services";
import { Spacer } from "../../elements/Spacer";
import { Tabs } from "../../elements/Tabs";
import { Card, CardBody, Button, Alert, Container, ListGroup, ListGroupItem } from "reactstrap";

interface MyQuizzesPageProps extends RouteComponentProps {
  user: RegisteredUserDTO;
}

type Quiz = AppQuizAssignment | QuizAttemptDTO;

interface QuizAssignmentProps {
  item: Quiz;
}

enum Status {
  Unstarted,
  Started,
  Complete,
}

function QuizItem({ item }: QuizAssignmentProps) {
  const assignment = isAttempt(item) ? null : item;
  const attempt = isAttempt(item) ? item : assignment?.attempt;
  const status: Status = !attempt ? Status.Unstarted : !attempt.completedDate ? Status.Started : Status.Complete;
  return (
    <div className="p-2">
      <Card className="card-neat">
        <CardBody>
          <h4 className="border-bottom pb-3 mb-3">{item.quizSummary?.title || item.quizId}</h4>

          {assignment ? (
            <p>
              {assignment.dueDate && (
                <>
                  Due date: <strong>{formatDate(assignment.dueDate)}</strong>
                </>
              )}
            </p>
          ) : (
            attempt && <p>Freely {status === Status.Started ? "attempting" : "attempted"}</p>
          )}
          {assignment && (
            <p>
              Set: {formatDate(assignment.creationDate)}
              {assignment.assignerSummary && <> by {extractTeacherName(assignment.assignerSummary)}</>}
            </p>
          )}
          {attempt && (
            <p>
              {status === Status.Complete
                ? `Completed: ${formatDate(attempt.completedDate)}`
                : `Started: ${formatDate(attempt.startDate)}`}
            </p>
          )}

          <div className="text-center mt-4">
            {assignment ? (
              <>
                {status === Status.Unstarted && (
                  <Button tag={Link} to={`/test/assignment/${assignment.id}`}>
                    Start test
                  </Button>
                )}
                {status === Status.Started && (
                  <Button tag={Link} to={`/test/assignment/${assignment.id}`}>
                    Continue test
                  </Button>
                )}
                {status === Status.Complete &&
                  (assignment.quizFeedbackMode !== "NONE" ? (
                    <Button tag={Link} to={`/test/attempt/${assignment.attempt?.id}/feedback`}>
                      View feedback
                    </Button>
                  ) : (
                    <strong>No feedback available</strong>
                  ))}
              </>
            ) : (
              attempt && (
                <>
                  {status === Status.Started && (
                    <Button tag={Link} to={`/test/attempt/${attempt.quizId}`}>
                      Continue test
                    </Button>
                  )}
                  {status === Status.Complete &&
                    (attempt.feedbackMode !== "NONE" ? (
                      <Button tag={Link} to={`/test/attempt/${attempt.id}/feedback`}>
                        View feedback
                      </Button>
                    ) : (
                      <strong>No feedback available</strong>
                    ))}
                </>
              )
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

interface AssignmentGridProps {
  quizzes: Quiz[];
  empty: string;
}

function QuizGrid({ quizzes, empty }: AssignmentGridProps) {
  return (
    <>
      {quizzes.length === 0 && <p>{empty}</p>}
      {quizzes.length > 0 && (
        <div className="block-grid-xs-1 block-grid-md-2 block-grid-lg-3 my-2">
          {quizzes.map((item) => (
            <QuizItem key={(isAttempt(item) ? "at" : "as") + item.id} item={item} />
          ))}
        </div>
      )}
    </>
  );
}

const MyQuizzesPageComponent = ({ user }: MyQuizzesPageProps) => {
  const quizAssignments = useAppSelector(selectors.quizzes.assignedToMe);
  const freeAttempts = useAppSelector(selectors.quizzes.attemptedFreelyByMe);
  const quizzes = useAppSelector(selectors.quizzes.available);

  const dispatch = useAppDispatch();

  const startIndex = 0;

  useEffect(() => {
    dispatch(loadQuizzes(startIndex));
    dispatch(loadQuizAssignedToMe());
    dispatch(loadQuizzesAttemptedFreelyByMe());
  }, [dispatch, startIndex]);

  const pageHelp = (
    <span>
      Use this page to see tests you need to take and your test results.
      <br />
      You can also take some tests freely whenever you want to test your knowledge.
    </span>
  );

  const assignmentsAndAttempts = [
    ...(isFound(quizAssignments) ? quizAssignments : []),
    ...(isFound(freeAttempts) ? freeAttempts : []),
  ];
  const [completedQuizzes, incompleteQuizzes] = partitionCompleteAndIncompleteQuizzes(assignmentsAndAttempts);

  const showQuiz = (quiz: QuizSummaryDTO) => {
    switch (user.role) {
      // Tutors should see the same tests as students can
      case "STUDENT":
      case "TUTOR":
        return (quiz.hiddenFromRoles && !quiz.hiddenFromRoles?.includes("STUDENT")) || quiz.visibleToStudents;
      case "TEACHER":
        return (quiz.hiddenFromRoles && !quiz.hiddenFromRoles?.includes("TEACHER")) ?? true;
      default:
        return true;
    }
  };

  return (
    <Container>
      <TitleAndBreadcrumb currentPageTitle="My tests" help={pageHelp} />

      <Tabs className="mb-5 mt-4" tabContentClass="mt-4">
        {{
          ["In progress tests"]: (
            <ShowLoading
              until={quizAssignments}
              ifNotFound={
                <Alert color="warning">Your test assignments failed to load, please try refreshing the page.</Alert>
              }
            >
              <QuizGrid quizzes={incompleteQuizzes} empty="You don't have any incomplete or assigned tests." />
            </ShowLoading>
          ),

          ["Completed tests"]: (
            <ShowLoading
              until={quizAssignments}
              ifNotFound={
                <Alert color="warning">Your test assignments failed to load, please try refreshing the page.</Alert>
              }
            >
              <QuizGrid quizzes={completedQuizzes} empty="You haven't completed any tests." />
            </ShowLoading>
          ),

          ["Practice tests"]: (
            <ShowLoading until={quizzes}>
              {quizzes && (
                <>
                  {quizzes.length === 0 && (
                    <p>
                      <em>There are no tests currently available.</em>
                    </p>
                  )}
                  <ListGroup className="mb-3 quiz-list">
                    {quizzes.filter(showQuiz).map((quiz) => (
                      <ListGroupItem className="p-0 bg-transparent" key={quiz.id}>
                        <div className="d-flex flex-grow-1 flex-column flex-sm-row align-items-center p-3">
                          <span className="mb-2 mb-sm-0">{quiz.title}</span>
                          {quiz.summary && <div className="small text-muted d-none d-md-block">{quiz.summary}</div>}
                          <Spacer />
                          {isTutorOrAbove(user) && (
                            <div className="d-none d-md-flex align-items-center mr-4">
                              <Link to={{ pathname: `/test/preview/${quiz.id}` }}>
                                <span>Preview</span>
                              </Link>
                            </div>
                          )}
                          <Button tag={Link} to={{ pathname: `/test/attempt/${quiz.id}` }}>
                            Take test
                          </Button>
                        </div>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </>
              )}
            </ShowLoading>
          ),
        }}
      </Tabs>
    </Container>
  );
};

export const MyQuizzes = withRouter(MyQuizzesPageComponent);
